import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Layout } from "@/components/layout";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDate } from "@/lib/date-utils";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { ServiceDialog } from "@/components/services/ServiceDialog";
import { useServices } from "@/hooks/use-services";
import { useOrganization } from "@clerk/clerk-react";

import type { Service } from "@onsemble/shared";

export default function ServicesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [isAddingService, setIsAddingService] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);

  // Get services from backend
  const { services, isLoading } = useServices();

  // Get organization ID from Clerk
  const { organization } = useOrganization();

  // Delete service mutation is the only one we need to keep
  // since the ServiceDialog component handles adding and updating services

  // Delete service mutation
  const deleteService = useMutation({
    mutationFn: async (_serviceId: string) => {
      // This would need a DELETE endpoint on the server that doesn't exist yet
      // This is a placeholder for future functionality
      toast({
        title: t("pages.admin.services.deleteService"),
        description: t("pages.admin.services.thisFunctionalityIsNot"),
        variant: "destructive",
      });
      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/services"] });
      setIsDeleteDialogOpen(false);
      setServiceToDelete(null);
    },
  });

  // Handle deleting a service
  const handleDeleteService = () => {
    if (!serviceToDelete) return;
    deleteService.mutate(serviceToDelete.uid);
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (service: Service) => {
    setServiceToDelete(service);
    setIsDeleteDialogOpen(true);
  };

  return (
    <Layout headerContent={
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">{t("common.admin")}</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">{t("common.services")}</span>
      </div>
    }>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">{t("common.services")}</h1>
            <p className="text-muted-foreground">
              {t("pages.admin.services.manageYourServiceOfferings")}
            </p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("pages.admin.services.backToAdmin")}
            </Button>
            <Button onClick={() => setIsAddingService(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t("pages.admin.services.addService")}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center p-8">
            <p>{t("pages.admin.services.loadingServices")}</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {services && services.length > 0 ? (
              services.map((service: Service) => (
                <Card key={service.uid} className="flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-xl font-semibold">
                        {service.title}
                      </CardTitle>
                      <Badge
                        variant="default"
                        className="bg-green-600 text-white"
                      >
                        ${formatCentsAsDollars(service.cost)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    {service.description && (
                      <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                        {service.description}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {t("pages.admin.services.addedOn")} {formatDate(service.createdAt)}
                    </p>
                  </CardContent>
                  <CardFooter className="flex justify-end space-x-2 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingService(service);
                      }}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      {t("pages.admin.services.edit")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(service)}
                      className="h-8 w-8"
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </CardFooter>
                </Card>
              ))
            ) : (
              <div className="col-span-full p-8 text-center bg-muted rounded-lg">
                <p className="text-muted-foreground">
                  {t("pages.admin.services.noServicesFoundAdd")}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Service Dialog */}
      <ServiceDialog
        isOpen={isAddingService}
        onClose={() => setIsAddingService(false)}
        mode="add"
        orgId={organization?.id || ""}
        onServiceAdded={() => {
          // Service added successfully
          queryClient.invalidateQueries({ queryKey: ["/api/services"] });
        }}
      />

      {/* Edit Service Dialog */}
      <ServiceDialog
        isOpen={!!editingService}
        onClose={() => setEditingService(null)}
        mode="edit"
        service={editingService || undefined}
        orgId={organization?.id || ""}
        onServiceUpdated={() => {
          // Service updated successfully
          queryClient.invalidateQueries({ queryKey: ["/api/services"] });
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("pages.admin.services.deleteService_2")}</DialogTitle>
            <DialogDescription>
              {t("pages.admin.services.areYouSureYou")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {serviceToDelete && (
              <div className="p-4 border rounded-md">
                <p className="font-semibold">{serviceToDelete.title}</p>
                {serviceToDelete.description && (
                  <p className="text-sm text-gray-700 mt-1">
                    {serviceToDelete.description}
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-1">
                  {t("pages.admin.services.cost")}{formatCentsAsDollars(serviceToDelete.cost)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button variant="destructive" onClick={handleDeleteService}>
              {t("pages.admin.services.deleteService_2")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
